import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./PlantationOneBannerOne.scss";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import { projectStorage, projectFirestore } from "../../../../firebase-config/config";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import firebase from "firebase/app";
import PGLoaders from "../../../../default-pages/PGLoaders/PGLoaders";


const PlantationOneBannerOne = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { addDocument } = useFirestore("media");
  const { documents: mediaDocuments } = useCollection("media");
  const { documents: bannerTexts } = useCollection("bannerTexts");

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadType, setUploadType] = useState("");
  const [isEditing, setIsEditing] = useState(false);
const bannerRef = useRef(null);

  const [showMediaPopup, setShowMediaPopup] = useState(false);
  const [videoState, setVideoState] = useState({
    loading: true,
    error: false
  });

  const [textContent, setTextContent] = useState({
    subheading: "CONNECT WITH NATURE",
    heading: "Why Should You Adopt a Tree?",
    text: "Sometimes, we plant a tree not just for the planet, but for the heart. A quiet way to remember, to honor, or to hold on to something that matters.",
  });

  const videoRef = useRef(null);
  const isVideoVisible = useRef(false);
  const observerRef = useRef(null);

  const bannerMedia = mediaDocuments?.filter(
    (doc) => doc.category === "plantation-banner"
  );

  const latestMedia = bannerMedia
    ?.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis())[0];

  useEffect(() => {
    if (bannerTexts?.length > 0) {
      const plantationBanner = bannerTexts.find((doc) => doc.id === "plantationBanner");
      if (plantationBanner) {
        setTextContent({
          subheading: plantationBanner.subheading || "",
          heading: plantationBanner.heading || "",
          text: plantationBanner.text || "",
        });
      }
    }
  }, [bannerTexts]);

  // Auto play/pause logic + tab visibility handling
  // Auto play/pause logic + tab visibility handling
useEffect(() => {
  const videoElement = videoRef.current;
  const bannerElement = bannerRef.current;
  if (!videoElement || !bannerElement || !latestMedia || latestMedia.mediaType !== "video") {
    setVideoState({ loading: false, error: false });
    return;
  }

  setVideoState({ loading: true, error: false });

  const handleLoadedData = () => {
    setVideoState({ loading: false, error: false });
  };

  const handleError = () => {
    setVideoState({ loading: false, error: true });
  };

  const playIfPossible = async () => {
    if (isVideoVisible.current && !document.hidden) {
      try {
        await videoElement.play();
      } catch (error) {
        console.log("Playback error:", error);
      }
    }
  };

  const pauseVideo = () => {
    videoElement.pause();
  };

  const handleIntersection = (entries) => {
    entries.forEach(entry => {
      isVideoVisible.current = entry.isIntersecting;
      if (entry.isIntersecting && !document.hidden) {
        playIfPossible();
      } else {
        pauseVideo();
      }
    });
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      pauseVideo();
    } else if (isVideoVisible.current) {
      playIfPossible();
    }
  };

  videoElement.addEventListener('loadeddata', handleLoadedData);
  videoElement.addEventListener('canplay', handleLoadedData);
  videoElement.addEventListener('error', handleError);

  document.addEventListener("visibilitychange", handleVisibilityChange);

  videoElement.src = latestMedia.url;
  videoElement.load();

  observerRef.current = new IntersectionObserver(handleIntersection, {
    threshold: 0.5
  });
  observerRef.current.observe(bannerElement); // ✅ observe the banner, not the video

  return () => {
    videoElement.removeEventListener('loadeddata', handleLoadedData);
    videoElement.removeEventListener('canplay', handleLoadedData);
    videoElement.removeEventListener('error', handleError);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  };
}, [latestMedia]);


  const handleAdoptTreeClick = () => navigate("/products-two");
  const handlePurposeClick = () => (window.location.href = "/#adopt-purpose-section");

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      alert("Please select a file smaller than 20MB for faster upload");
      return;
    }

    const fileType = file.type.includes("video") ? "video" : "image";
    const storagePath = `${fileType}s/${Date.now()}_${file.name}`;
    const storageRef = projectStorage.ref(storagePath);
    const uploadTask = storageRef.put(file);

    setUploading(true);
    setUploadType(fileType);
    setUploadProgress(0);
    setShowMediaPopup(false);

    try {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          setUploading(false);
        },
        async () => {
          const url = await uploadTask.snapshot.ref.getDownloadURL();
          
          await addDocument({
            url,
            name: file.name,
            type: file.type,
            size: file.size,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            category: "plantation-banner",
            mediaType: fileType,
            optimized: true
          });
          
          setUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (err) {
      console.error("Upload error:", err);
      setUploading(false);
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setTextContent((prev) => ({ ...prev, [name]: value }));
  };

  const saveTextChanges = async () => {
    try {
      const docRef = projectFirestore.collection("bannerTexts").doc("plantationBanner");
      await docRef.set(
        {
          ...textContent,
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating banner text:", err);
    }
  };

  return (
<section ref={bannerRef} className="plantation-nature-banner nature-section">
      <div className="banner-bg">
        {latestMedia ? (
          latestMedia.mediaType === "video" ? (
            <>
              <video
                ref={videoRef}
                className="nature-bg-video"
                src={latestMedia.url}
                autoPlay
                loop
                playsInline
                preload="auto"
              />
              {videoState.loading && (
                <div className="video-loader">
                  <img
                    src="https://i.pinimg.com/originals/87/e9/35/87e935c85648e03d1fd8abd4569ca81b.gif"
                    alt="Loading..."
                    className="loader-spinner"
                  />
                  <p>Loading video...</p>
                </div>
              )}
              {videoState.error && (
                <div className="video-error">
                  <p>Failed to load video</p>
                  <button onClick={() => window.location.reload()}>Retry</button>
                </div>
              )}
            </>
          ) : (
            <img
              className="nature-bg-image"
              src={latestMedia.url}
              alt="Banner background"
            />
          )
        ) : null}
        <div className="nature-bg-overlay"></div>
      </div>

      <div className="banner-content nature-text-center">
        {isEditing ? (
          <div className="text-edit-form">
            <div className="form-group">
              <label>Subheading:</label>
              <input
                type="text"
                name="subheading"
                value={textContent.subheading}
                onChange={handleTextChange}
              />
            </div>
            <div className="form-group">
              <label>Heading:</label>
              <input
                type="text"
                name="heading"
                value={textContent.heading}
                onChange={handleTextChange}
              />
            </div>
            <div className="form-group">
              <label>Text:</label>
              <textarea
                name="text"
                value={textContent.text}
                onChange={handleTextChange}
                rows="3"
              />
            </div>
            <div className="edit-buttons">
              <button
                className="nature-btn nature-btn--primary"
                onClick={saveTextChanges}
              >
                Save Changes
              </button>
              <button
                className="nature-btn nature-btn--primary"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          (textContent.subheading || textContent.heading || textContent.text) && (
            <>
              {textContent.subheading && (
                <p className="nature-subheading">
                  <h3>{textContent.subheading}</h3>
                  <div className="nature-subheading__underline"></div>
                </p>
              )}
              {textContent.heading && <h1 className="nature-heading">{textContent.heading}</h1>}
              {textContent.text && <p className="nature-text">{textContent.text}</p>}

              <div className="nature-btn-group">
                <button
                  className="nature-btn nature-btn--primary"
                  onClick={handleAdoptTreeClick}
                >
                  <h5>Adopt a Tree Now</h5>
                  <div className="btn-shimmer"></div>
                </button>

                <button
                  className="nature-btn nature-btn--secondary"
                  onClick={handlePurposeClick}
                >
                  <h5>Purpose to Adopt</h5>
                </button>
              </div>
            </>
          )
        )}
      </div>

      {user && user.role === "admin" && !isEditing && (
        <div className="admin-controls-container">
          <button
            className="edit-banner-button"
            onClick={() => setShowMediaPopup(!showMediaPopup)}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Change Banner"}
          </button>

          {showMediaPopup && (
            <div className="media-upload-popup">
              <label className="media-upload-option">
                Change Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
              </label>
              <label className="media-upload-option">
                Change Video
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
              </label>
              <button
                className="close-popup-button"
                onClick={() => setShowMediaPopup(false)}
              >
                Close
              </button>
            </div>
          )}

          <button
            className="edit-text-button"
            onClick={() => setIsEditing(true)}
          >
            Edit Text
          </button>
        </div>
      )}

      {uploading && (
        <div className="upload-progress-overlay">
          <div className="upload-progress-container">
            <h4><PGLoaders/> {uploadType}...</h4>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span>{Math.round(uploadProgress)}%</span>
            <p>This should only take a few seconds...</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlantationOneBannerOne;
